import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import useUserInfo from './hooks/useUserInfo';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Navbar from './components/Navbar';
import CircleIcon from '@mui/icons-material/Circle';
import { Alert, Link, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

const Dashboard = () => {
    const apiUrl = process.env.REACT_APP_BACKEND_API;
    const userInfo = useUserInfo(apiUrl);
    const navigate = useNavigate();
    
    const [notes, setNotes] = useState([]);

    const [newNoteTitle, setNewNoteTitle] = useState('');

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedNoteID, setSelectedNoteID] = useState(null);
    const [screenLoading, setScreenLoading] = useState(true);
    const open = Boolean(anchorEl);
    const handleClick = (event, noteID) => {
        setAnchorEl(event.currentTarget);
        setSelectedNoteID(noteID);
    }

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDelete = () => {
        handleDeleteNote(selectedNoteID);
        handleClose();
    };

    useEffect(() => {
        const fetchNotes = async () => {
            try {
                const response = await fetch(`${apiUrl}/users/get_notes_list`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.token}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setNotes(data);
                } else {
                    console.error('Failed to fetch notes');
                }
            } catch (error) {
                console.error('Error:', error);
            }
            setScreenLoading(false);
        };

        if (localStorage.token) {
            fetchNotes();
        }
    }, [apiUrl, userInfo.token]);

    

    const handleCreateNote = async () => {
        try {
            const response = await fetch(`${apiUrl}/users/create_note`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.token}`
                },
                body: JSON.stringify({ title: newNoteTitle })
            });
            if (response.ok) {
                console.error('Note created');
                window.location.reload();
            } else {
                console.error('Failed to create note');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleDeleteNote = async (noteID) => {
        try {
            const response = await fetch(`${apiUrl}/users/delete_note/${noteID}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.token}`
                },
            });
            if (response.ok) {
                console.error('Note deleted');
                window.location.reload();
            } else {
                console.error('Failed to delete note');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleCreateNewNote = async () => {
        try {
            // Get current notes before creating
            const beforeResponse = await fetch(`${apiUrl}/users/get_notes_list`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.token}`
                }
            });
            const beforeNotes = beforeResponse.ok ? await beforeResponse.json() : [];
            const beforeNoteIds = new Set(beforeNotes.map(note => note.id));

            // Create the new note
            const response = await fetch(`${apiUrl}/users/create_note`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.token}`
                },
                body: JSON.stringify({ title: 'Untitled' })
            });
            
            if (response.ok) {
                // Fetch notes list again to find the new note
                const notesResponse = await fetch(`${apiUrl}/users/get_notes_list`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.token}`
                    }
                });
                
                if (notesResponse.ok) {
                    const notesData = await notesResponse.json();
                    // Find the note that wasn't in the before list
                    const newNote = notesData.find(note => !beforeNoteIds.has(note.id));
                    
                    if (newNote) {
                        navigate(`/note/${newNote.id}`);
                    } else {
                        // Fallback: if we can't find it, use the first note with "Untitled" title
                        const untitledNote = notesData.find(note => note.title === 'Untitled');
                        if (untitledNote) {
                            navigate(`/note/${untitledNote.id}`);
                        }
                    }
                }
            } else {
                console.error('Failed to create note');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };


    const theme = useTheme();

    if (userInfo.is_loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (userInfo.is_authenticated === false) {
        window.location.href = "/signin";
        return null;
    }

    return (
        <>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                padding: theme.spacing(1, 2),
            }}>
                <Navbar 
                    rightContent={
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={<AddIcon />}
                            onClick={handleCreateNewNote}
                            sx={{
                                flexShrink: 0,
                            }}
                        >
                            New Note
                        </Button>
                    }
                />
            </Box>
            {screenLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Container sx={{ 
                    marginTop: theme.spacing(3), 
                    maxWidth: { xs: '100%', sm: '800px' },
                    paddingX: theme.spacing(3),
                }}>
                    <Typography
                        variant="h3"
                        sx={{
                            marginBottom: theme.spacing(3),
                            fontWeight: theme.typography.h3.fontWeight,
                        }}
                    >
                        Welcome {userInfo.first_name}
                    </Typography>
                    
                    <Box sx={{ marginBottom: theme.spacing(4) }}>
                        {notes.length > 0 ? (
                            notes.map((note) => (
                                <Box
                                    key={note.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: theme.spacing(0.75, 1.5),
                                        marginBottom: theme.spacing(0.5),
                                        borderRadius: '6px',
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <CircleIcon
                                        onClick={(event) => handleClick(event, note.id)}
                                        sx={{
                                            color: 'primary.main',
                                            fontSize: '16px',
                                            padding: theme.spacing(0.5), // 4px
                                            cursor: 'pointer',
                                            borderRadius: '50%',
                                            marginRight: theme.spacing(1.5),
                                            flexShrink: 0,
                                            alignSelf: 'flex-start',
                                            marginTop: theme.spacing(0.5), // 4px
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                                transform: 'scale(1.2)',
                                            },
                                        }}
                                    />
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={open}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                        PaperProps={{
                                            sx: {
                                                minWidth: '120px',
                                                borderRadius: '6px',
                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                marginTop: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        <MenuItem
                                            onClick={() => handleDelete(note.id)}
                                            sx={{
                                                fontSize: '14px',
                                                padding: theme.spacing(0.5, 1.25),
                                                minHeight: 'auto',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                },
                                            }}
                                        >
                                            Delete
                                        </MenuItem>
                                    </Menu>
                                    <Typography
                                        component={Link}
                                        href={`/note/${note.id}`}
                                        variant="body1"
                                        sx={{
                                            color: 'text.primary',
                                            textDecoration: 'none',
                                            fontWeight: 400,
                                            fontSize: '15px',
                                            flex: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.5,
                                            alignSelf: 'center',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {note.title || 'Untitled'}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    padding: theme.spacing(2),
                                    textAlign: 'center',
                                }}
                            >
                                No notes yet. Create your first note below.
                            </Typography>
                        )}
                    </Box>
                    
                    <Box
                        sx={{
                            display: 'flex',
                            gap: theme.spacing(1.5),
                            alignItems: 'center',
                            marginTop: theme.spacing(4),
                            paddingTop: theme.spacing(3),
                            borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <TextField
                            label="Add new page"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={newNoteTitle}
                            onChange={(e) => setNewNoteTitle(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleCreateNote();
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '14px',
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            onClick={handleCreateNote}
                            sx={{
                                flexShrink: 0,
                                minWidth: '80px',
                            }}
                        >
                            Add
                        </Button>
                    </Box>
                </Container>
            )}
        </>
    );
};

export default Dashboard;

